import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  buildUploadFilePath,
  FileUploadDropzoneDirective,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  IReferral,
  ReferralActor,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';
import { ReferralMediaService } from '../../services/referral-media.service';

@Component({
  selector: 'pr-referral-image-upload',
  imports: [
    NgCommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FileUploadDropzoneDirective,
  ],
  templateUrl: './referral-image-upload.component.html',
  styleUrl: './referral-image-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col h-full relative',
  },
})
export class ReferralImageUploadComponent {
  private _storage = inject(Storage);
  private _referralMedia = inject(ReferralMediaService);

  readonly referral = input.required<WithRef<IReferral>>();
  readonly currentUser = input.required<ReferralActor>();

  readonly isHovering = signal(false);

  onHovered(hovering: boolean): void {
    this.isHovering.set(hovering);
  }

  async onDropped(files: FileList): Promise<void> {
    if (!files || files.length === 0) {
      return;
    }

    await this._uploadFiles(Array.from(files));
  }

  private async _uploadFiles(files: File[]): Promise<void> {
    const referral = this.referral();
    const actor = this.currentUser();

    if (!referral?.ref?.path || !actor) {
      return;
    }

    for (const file of files) {
      const storagePath = buildUploadFilePath(
        referral.ref.path,
        file.name,
        true
      );

      const uploadTask = uploadBytesResumable(
        ref(this._storage, storagePath),
        file
      );

      await this._referralMedia.add(
        {
          task: uploadTask,
          file,
          storagePath,
        },
        referral,
        actor
      );
    }
  }
}
